import React from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav";
import { ArrowRight, BookText, Check, FileText, Sparkles, BookOpen, Users } from "lucide-react";
import { motion } from "framer-motion";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/8a401958-48e1-480d-8e1f-21a4f6908dd9/images/0833ce1b8dbd962c42b149dfa4d8ee938de89ffee7d2b59e18732d8bcce166e2.png";
const FEATURE_IMG_1 = "https://images.unsplash.com/photo-1711185896337-ee0ca611c5de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const FEATURE_IMG_2 = "https://static.prod-images.emergentagent.com/jobs/8a401958-48e1-480d-8e1f-21a4f6908dd9/images/ca9cb06cd5c80425b49bfbac661f18d2f4a0273823814d2b245dfa355987285f.png";

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

const Landing = () => {
  return (
    <div className="min-h-screen bg-[#FAFAF9] text-[#2C2C2A]">
      <TopNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-[0.55]"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(to bottom, black 0%, black 60%, transparent 100%)",
          }}
        />
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-20 lg:pb-32">
          <motion.div {...fadeUp} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/60 backdrop-blur border border-[#D3D1C7] text-xs uppercase tracking-[0.2em] text-[#5F5E5A]" data-testid="hero-eyebrow">
              <Sparkles className="w-3 h-3 text-[#534AB7]" /> Built for KDP self-publishers
            </span>
            <h1
              data-testid="hero-headline"
              className="mt-7 text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-[#2C2C2A]"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
            >
              Your publishing toolkit,
              <br />
              <span className="italic text-[#534AB7]">all in one place.</span>
            </h1>
            <p className="mt-7 text-lg text-[#5F5E5A] leading-relaxed max-w-2xl">
              PublishMate handles your Amazon listing, your full-wrap cover — front, spine, and back —
              and your front matter. Paperback and hardcover. No more juggling five different tools.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 items-center">
              <Link
                to="/signup"
                data-testid="hero-cta"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-[8px] bg-[#534AB7] text-white font-medium hover:bg-[#3C3489] transition-all shadow-sm"
              >
                Start free — no card needed
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-[8px] border border-[#D3D1C7] hover:bg-[#F1EFE8] transition-colors text-[#2C2C2A]"
                data-testid="hero-pricing-link"
              >
                See pricing
              </Link>
            </div>
            <p className="mt-5 text-xs text-[#5F5E5A] tracking-wide">
              3 free listings · 1 free cover project · cancel anytime.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Social proof bar */}
      <section className="border-t border-b border-[#D3D1C7]/60 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-5 flex flex-wrap items-center justify-center gap-8 text-sm text-[#5F5E5A]">
          {[
            "✅ Paperback & hardcover",
            "✅ KDP-spec bleed & safe zones",
            "✅ AI listing generator",
            "✅ DALL·E cover image generation",
            "✅ Full-wrap PDF export",
          ].map((item) => (
            <span key={item} className="tracking-wide">{item}</span>
          ))}
        </div>
      </section>

      {/* Feature 1: Listing Generator */}
      <section className="border-b border-[#D3D1C7]/60">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-[#534AB7]">Tool 01</div>
            <h2
              className="mt-3 text-4xl lg:text-5xl tracking-tight leading-[1.05]"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
            >
              Listing Generator
            </h2>
            <p className="mt-5 text-[#5F5E5A] leading-relaxed">
              Punch in your title, genre, and a paragraph about the book. Get a publish-ready Amazon listing
              with description, seven keywords, three browse categories, and two alternative headlines —
              all written for the A9 search algorithm.
            </p>
            <ul className="mt-7 space-y-3 text-sm">
              {[
                "Amazon-supported HTML description (400–600 words)",
                "Seven keyword phrases optimised for KDP",
                "Three real Amazon browse categories",
                "Two opening-hook variations to A/B test",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-[#1D9E75] mt-0.5 shrink-0" strokeWidth={2} />
                  <span className="text-[#2C2C2A]">{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fadeUp} transition={{ duration: 0.65, delay: 0.1 }} className="lg:col-span-7">
            <div className="bg-white rounded-[12px] border border-[#D3D1C7] shadow-[0_18px_60px_-25px_rgba(60,52,137,0.25)] overflow-hidden">
              <div className="px-5 py-3 border-b border-[#D3D1C7] flex items-center gap-2 bg-[#FAFAF9]">
                <FileText className="w-4 h-4 text-[#534AB7]" strokeWidth={1.6} />
                <span className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A]">listing.html</span>
              </div>
              <div className="p-7 grid sm:grid-cols-2 gap-5 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#5F5E5A] mb-2">Description</div>
                  <div className="space-y-1.5 text-[#2C2C2A] leading-relaxed">
                    <p className="font-semibold">Tired of boring maths workbooks your kid throws across the room?</p>
                    <p>Maths Monsters: Year 2 turns every page into a puzzle, every problem into a quest…</p>
                  </div>
                </div>
                <div className="space-y-5">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#5F5E5A] mb-2">7 Keywords</div>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        "year 2 maths workbook",
                        "ks1 maths practice",
                        "fun maths for kids",
                        "primary school maths",
                        "addition subtraction kids",
                        "homeschool maths",
                        "7 year old workbook",
                      ].map((k) => (
                        <span key={k} className="px-2 py-1 rounded-md bg-[#F1EFE8] text-[#2C2C2A] text-xs border border-[#D3D1C7]">
                          {k}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#5F5E5A] mb-2">3 Categories</div>
                    <div className="space-y-1 text-xs text-[#2C2C2A]">
                      <div>Books › Children's Books › Education › Mathematics</div>
                      <div>Books › Children's Books › Workbooks</div>
                      <div>Books › Children's Books › Activity Books</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature 2: Cover Creator */}
      <section className="border-b border-[#D3D1C7]/60 bg-[#F1EFE8]/40">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div {...fadeUp} className="lg:col-span-7 lg:order-1 order-2">
            <div className="rounded-[12px] overflow-hidden border border-[#D3D1C7] shadow-[0_25px_70px_-20px_rgba(60,52,137,0.3)]">
              <img src={FEATURE_IMG_2} alt="Cover Creator preview" className="w-full h-[420px] object-cover" />
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ duration: 0.65, delay: 0.1 }} className="lg:col-span-5 lg:order-2 order-1">
            <div className="text-xs uppercase tracking-[0.25em] text-[#534AB7]">Tool 02</div>
            <h2
              className="mt-3 text-4xl lg:text-5xl tracking-tight leading-[1.05]"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
            >
              Cover Creator
            </h2>
            <p className="mt-5 text-[#5F5E5A] leading-relaxed">
              A canvas built specifically for KDP. Design your front, spine and back panels with proper
              bleed and safe-zone guides. Supports both paperback and hardcover specs — auto spine width,
              AI image generation, and barcode-aware back cover included.
            </p>
            <ul className="mt-7 space-y-3 text-sm">
              {[
                "Paperback & hardcover — KDP-correct bleed for both",
                "Auto-calculated spine width from page count & paper type",
                "DALL·E 3 AI image generation built into the editor",
                "Export full-wrap PDF ready for KDP print upload",
                "Background auto-matches your cover image colours",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-[#1D9E75] mt-0.5 shrink-0" strokeWidth={2} />
                  <span className="text-[#2C2C2A]">{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Feature 3: Front Matter */}
      <section className="border-b border-[#D3D1C7]/60">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-[#534AB7]">Tool 03</div>
            <h2
              className="mt-3 text-4xl lg:text-5xl tracking-tight leading-[1.05]"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
            >
              Front Matter Generator
            </h2>
            <p className="mt-5 text-[#5F5E5A] leading-relaxed">
              Copyright page, dedication, disclaimer, table of contents — generated and exported as
              a ready-to-paste Word doc or PDF. No more googling "what goes in the front matter of a self-published book."
            </p>
            <ul className="mt-7 space-y-3 text-sm">
              {[
                "Copyright page with correct legal language",
                "ISBN & ASIN field support",
                "Export as DOCX or PDF — paste straight into your manuscript",
                "Status auto-updates to final once ISBN is added",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-[#1D9E75] mt-0.5 shrink-0" strokeWidth={2} />
                  <span className="text-[#2C2C2A]">{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fadeUp} transition={{ duration: 0.65, delay: 0.1 }} className="lg:col-span-7">
            <div className="bg-white rounded-[12px] border border-[#D3D1C7] shadow-[0_18px_60px_-25px_rgba(60,52,137,0.25)] overflow-hidden">
              <div className="px-5 py-3 border-b border-[#D3D1C7] flex items-center gap-2 bg-[#FAFAF9]">
                <BookOpen className="w-4 h-4 text-[#534AB7]" strokeWidth={1.6} />
                <span className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A]">front-matter.docx</span>
              </div>
              <div className="p-7 space-y-4 text-sm text-[#2C2C2A]">
                <div className="border-b border-[#D3D1C7] pb-4">
                  <p className="text-xs uppercase tracking-wider text-[#5F5E5A] mb-2">Copyright Page</p>
                  <p className="leading-relaxed text-[#5F5E5A] italic">
                    Copyright © 2026 Kevin Montes. All rights reserved. No part of this publication may be reproduced…
                  </p>
                </div>
                <div className="border-b border-[#D3D1C7] pb-4">
                  <p className="text-xs uppercase tracking-wider text-[#5F5E5A] mb-2">Dedication</p>
                  <p className="leading-relaxed text-[#5F5E5A] italic">For everyone who said they'd write a book one day. Today is the day.</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#5F5E5A] mb-2">ISBN</p>
                  <p className="font-mono text-[#534AB7]">978-0-000000-00-0</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="bg-[#534AB7]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-20 lg:py-28 text-center">
          <motion.div {...fadeUp}>
            <h2
              className="text-4xl lg:text-5xl tracking-tight text-white leading-[1.05]"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
            >
              Start free. Scale when you're ready.
            </h2>
            <p className="mt-5 text-[#C5C1F0] text-lg max-w-xl mx-auto leading-relaxed">
              Try PublishMate with no card required. Upgrade to Solo, Pro, or Agency when your output demands it.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link
                to="/signup"
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-[8px] bg-white text-[#534AB7] font-medium hover:bg-[#F1EFE8] transition-all shadow-sm text-sm"
              >
                Get started free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-[8px] border border-white/30 text-white hover:bg-white/10 transition-colors text-sm"
              >
                View pricing
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#D3D1C7] bg-[#FAFAF9]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-[#534AB7] flex items-center justify-center">
              <BookText className="w-3.5 h-3.5 text-white" strokeWidth={1.8} />
            </div>
            <span className="font-medium text-[#2C2C2A] text-sm" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>PublishMate</span>
          </div>
          <p className="text-xs text-[#5F5E5A]">© {new Date().getFullYear()} PublishMate. Built for self-publishers.</p>
          <div className="flex gap-5 text-xs text-[#5F5E5A]">
            <Link to="/pricing" className="hover:text-[#534AB7] transition-colors">Pricing</Link>
            <Link to="/login" className="hover:text-[#534AB7] transition-colors">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
